import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Preloader from "@/components/Preloader";
import CustomCursor from "@/components/CustomCursor";
import Footer from "@/sections/Footer";
import Home from "@/pages/Home";
import Consultations from "@/pages/Consultations";
import ConsultationDetail from "@/pages/ConsultationDetail";
import ContactPage from "@/pages/ContactPage";

gsap.registerPlugin(ScrollTrigger);

const Overlays = () => {
  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      <div className="grid-bg" aria-hidden />
      <div className="noise" aria-hidden />
    </>,
    document.body
  );
};

const NavLink = ({ to, label, testId, external }) => {
  const Inner = (
    <span className="scramble" data-text={label}>
      <span>{label}</span>
    </span>
  );
  if (external) {
    return (
      <a href={to} data-testid={testId}>
        {Inner}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId}>
      {Inner}
    </Link>
  );
};

const Nav = () => (
  <nav
    className="fixed top-0 left-0 right-0 z-40 px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between"
    style={{
      backgroundColor: "rgba(242, 242, 240, 0.7)",
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
      borderBottom: "1px solid var(--hairline)",
    }}
    data-testid="site-nav"
  >
    <Link to="/" className="font-display font-bold tracking-tighter text-[#0a0a0b] text-xl" data-testid="nav-logo">
      DELVENTURE
    </Link>
    <ul className="hidden md:flex items-center gap-10 text-[11px] tracking-[0.28em] uppercase text-[#0a0a0b]">
      <li><NavLink to="/services" label="Services" testId="nav-services" /></li>
      <li><NavLink to="/#solutions" label="Solutions" testId="nav-solutions" external /></li>
      <li><NavLink to="/#process" label="Process" testId="nav-process" external /></li>
      <li><NavLink to="/contact" label="Contact" testId="nav-contact" /></li>
    </ul>
    <Link
      to="/contact"
      data-testid="nav-cta"
      className="text-[11px] tracking-[0.28em] uppercase text-[#0a0a0b] hairline-strong px-4 py-2 rounded-full"
    >
      Start
    </Link>
  </nav>
);

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

const Layout = ({ children }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    function raf(time) {
      lenis.raf(time);
      ScrollTrigger.update();
      requestAnimationFrame(raf);
    }
    const id = requestAnimationFrame(raf);
    lenis.on("scroll", ScrollTrigger.update);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
    };
  }, [loading]);

  return (
    <div className="relative">
      <Overlays />
      {loading && <Preloader onDone={() => setLoading(false)} />}
      <CustomCursor />
      <Nav />
      <ScrollManager />
      <main data-testid="main-content">{children}</main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Consultations />} />
            <Route path="/services/:slug" element={<ConsultationDetail />} />
            {/* Legacy paths kept as silent redirects via component logic */}
            <Route path="/consultations" element={<Consultations />} />
            <Route path="/consultations/:slug" element={<ConsultationDetail />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </div>
  );
}

export default App;
